import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useBoolean } from '@chakra-ui/react';
import { useLocalStorage } from 'usehooks-ts';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { useLoader, useStore } from 'common/hooks';
import { mapTaskInboxToTicket } from 'data/link/mappers/taskInbox.mapper';
import { TicketListItem } from 'models/link/Link';
import { TaskInbox } from 'models/link/Task';
import { ResponseError } from 'models';
import { TaskActivity } from 'data/link/schemas';
import { ReplyEmailSupport } from 'models/user/UserSupport';
import {
    cancelTaskActivityV2,
    closeTaskActivityV2,
    createTaskRemarkV2,
    getTaskInboxProgressivelyByLinkId,
    getTaskActivityV2,
    getUserAccountDataByEmailV2,
    updateTaskProgressV2
} from 'data/link/respositories/taskRepository';

import { TABS } from './TicketV2.view';
import { DateRange } from '../components';
import type { CommentReplyInput } from '../components/TicketDetailModal';

dayjs.extend(isBetween);

function TicketV2ViewModel() {
    const loader = useLoader();
    const { userCompanyStore } = useStore();
    const { email } = userCompanyStore;

    const [tasks, setTasks] = useState<TicketListItem[]>();
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isRefreshingTaskInbox, setIsRefreshingTaskInbox] = useState(false);
    const [linkId, setLinkId] = useState('');
    const [filter, setFilter] = useState<(typeof TABS)[number]['value']>('all');
    const [search, setSearch] = useState('');
    const [displayStyle, setDisplayStyle] = useLocalStorage<'grid' | 'list'>(
        'SUPPORT-DISPLAY_TICKET_STYLE',
        'grid'
    );
    const [dateRange, setDateRange] = useState<DateRange>({});
    const [showSupportModal, setShowSupportModal] = useBoolean();
    const [showCreateTicketModal, setShowCreateTicketModal] = useBoolean();
    const [selectedTask, setSelectedTask] = useState<TicketListItem>();
    const [selectedTaskDetail, setSelectedTaskDetail] = useState<TaskActivity>();
    const showTaskDetailModal = !!selectedTaskDetail;
    const taskInboxAbortController = useRef<AbortController>();
    const taskInboxRequestId = useRef(0);

    const deferredSearch = useDeferredValue(search);
    const deferredFilter = useDeferredValue(filter);
    const filteredTasks = useMemo(() => {
        if (!tasks) return null;

        const searchTerm = deferredSearch.toLowerCase();
        const isAllFilter = deferredFilter === 'all';

        const dateFromDayjs = dateRange.dateFrom && dayjs(dateRange.dateFrom, 'YYYYMMDD');
        const dateToDayjs = dateRange.dateTo && dayjs(dateRange.dateTo, 'YYYYMMDD');

        return tasks.filter(task => {
            const checkInDayjs = dayjs(task.checkIn_Date, 'YYYYMMDD');

            const matchesDateRange =
                !dateRange.dateTo && !dateRange.dateFrom
                    ? true
                    : dateRange.dateFrom && !dateRange.dateTo
                    ? checkInDayjs.isAfter(dateFromDayjs) || checkInDayjs.isSame(dateFromDayjs)
                    : dateRange.dateTo && !dateRange.dateFrom
                    ? checkInDayjs.isBefore(dateToDayjs) || checkInDayjs.isSame(dateFromDayjs)
                    : checkInDayjs.isBetween(dateFromDayjs, dateToDayjs);
            const matchesFilter =
                isAllFilter || task.activityOverviewStatus.includes(deferredFilter);
            const matchesKeyword =
                task.jobdescription.toLowerCase().includes(searchTerm) ||
                task.ticketNO.toLowerCase().includes(searchTerm);

            return matchesFilter && matchesKeyword && matchesDateRange;
        });
    }, [tasks, deferredFilter, deferredSearch, dateRange]);

    const beginTaskInboxRequest = () => {
        taskInboxAbortController.current?.abort();

        const abortController = new AbortController();
        const requestId = ++taskInboxRequestId.current;
        taskInboxAbortController.current = abortController;

        return { abortController, requestId };
    };

    const isCurrentTaskInboxRequest = (requestId: number) =>
        requestId === taskInboxRequestId.current;

    const cancelTaskInboxRequest = useCallback(() => {
        taskInboxAbortController.current?.abort();
        taskInboxAbortController.current = undefined;
        taskInboxRequestId.current += 1;
        setIsRefreshingTaskInbox(false);
        setIsLoadingMore(false);
        loader.hide();
    }, []);

    const loadTaskInbox = async (
        currentLinkId: string,
        requestId: number,
        abortController: AbortController,
        hideLoaderAfterFirstPage = true
    ) => {
        let hasDisplayedFirstPage = false;
        const applyTaskInboxItems = (taskInboxItems: TaskInbox[]) => {
            const mappedTasks = taskInboxItems.map(task =>
                mapTaskInboxToTicket(task, currentLinkId)
            );

            setTasks(mappedTasks);
            setSelectedTask(currentTask => {
                if (!currentTask) return currentTask;

                return (
                    mappedTasks.find(task => task.aobjectlink === currentTask.aobjectlink) ??
                    currentTask
                );
            });
        };
        const updateTasks = (taskInboxItems: TaskInbox[]) => {
            if (!isCurrentTaskInboxRequest(requestId) || abortController.signal.aborted) return;

            applyTaskInboxItems(taskInboxItems);

            if (!hasDisplayedFirstPage) {
                hasDisplayedFirstPage = true;
                setIsLoadingMore(true);
                if (hideLoaderAfterFirstPage) loader.hide();
            }
        };

        const [error, taskInboxItems] = await getTaskInboxProgressivelyByLinkId(
            currentLinkId,
            updateTasks,
            abortController.signal
        );
        if (error) throw new Error(error.message);

        if (isCurrentTaskInboxRequest(requestId) && !abortController.signal.aborted) {
            applyTaskInboxItems(taskInboxItems);
        }
    };

    const initializeTaskInbox = async (email: string) => {
        const { abortController, requestId } = beginTaskInboxRequest();

        try {
            loader.show();
            setIsLoadingMore(false);
            setLinkId('');
            setTasks(undefined);
            const [linkIdError, accountLinkId] = await getUserAccountDataByEmailV2(email);
            if (linkIdError) throw new Error(linkIdError.message);

            const currentLinkId = accountLinkId?.trim();
            if (!currentLinkId) throw new Error('ไม่พบ Link ID ของผู้ใช้งาน');

            setLinkId(currentLinkId);
            await loadTaskInbox(currentLinkId, requestId, abortController);
        } catch (error) {
            abortController.abort();
            if (axios.isCancel(error) || !isCurrentTaskInboxRequest(requestId)) return;

            setTasks(currentTasks => currentTasks ?? []);
            Swal.fire('Error!', (error as Error).message, 'error');
        } finally {
            if (isCurrentTaskInboxRequest(requestId)) {
                setIsLoadingMore(false);
                loader.hide();
            }
        }
    };

    const refreshTaskInbox = async (options?: { silent?: boolean }) => {
        if (!linkId || isRefreshingTaskInbox) return;

        const isSilent = options?.silent === true;
        const { abortController, requestId } = beginTaskInboxRequest();

        try {
            setIsRefreshingTaskInbox(true);
            if (!isSilent) loader.show();
            setIsLoadingMore(false);
            if (!isSilent) setTasks(undefined);
            await loadTaskInbox(linkId, requestId, abortController, !isSilent);
        } catch (error) {
            abortController.abort();
            if (axios.isCancel(error) || !isCurrentTaskInboxRequest(requestId)) return;

            if (!isSilent) Swal.fire('Error!', (error as Error).message, 'error');
        } finally {
            if (isCurrentTaskInboxRequest(requestId)) {
                setIsRefreshingTaskInbox(false);
                setIsLoadingMore(false);
                if (!isSilent) loader.hide();
            }
        }
    };

    useEffect(() => {
        if (!email) return;
        void initializeTaskInbox(email);

        return () => {
            taskInboxAbortController.current?.abort();
            taskInboxRequestId.current += 1;
            loader.hide();
        };
    }, [email]);

    const onReplyEmail = useCallback(
        async (reply: ReplyEmailSupport) => {
            try {
                loader.show();

                await axios.post('/v1/user/support/reply', reply);

                Swal.fire('Success!', 'Reply successfully!', 'success');
            } catch (error) {
                Swal.fire('Error!', (error as AxiosError).response?.data as string, 'error');
            } finally {
                handleCloseTicketDetailModal();
                await handleClickTicketDetail(selectedTask!);
                loader.hide();
            }
        },
        [selectedTask]
    );

    const handleClickTicketDetail = useCallback(async (task: TicketListItem) => {
        cancelTaskInboxRequest();

        try {
            loader.show();

            const [error, data] = await getTaskActivityV2({
                key: task.aobjectlink,
                link_id: task.empcode
            });
            if (error) {
                throw error;
            }

            setSelectedTask(task);
            setSelectedTaskDetail(data);
        } catch (error) {
            Swal.fire('Error!', (error as ResponseError).message, 'error');
        } finally {
            loader.hide();
        }
    }, [cancelTaskInboxRequest]);

    const handleOpenSupportModal = useCallback(() => {
        cancelTaskInboxRequest();
        setShowSupportModal.on();
    }, [cancelTaskInboxRequest, setShowSupportModal]);

    const handleOpenCreateTicketModal = useCallback(() => {
        cancelTaskInboxRequest();
        setShowCreateTicketModal.on();
    }, [cancelTaskInboxRequest, setShowCreateTicketModal]);

    const handleCloseTicketDetailModal = useCallback(() => {
        setSelectedTask(undefined);
        setSelectedTaskDetail(undefined);
    }, []);

    const handleClickCloseTask = useCallback(async () => {
        try {
            loader.show();

            if (!selectedTaskDetail) {
                return;
            }
            if (!selectedTask?.empcode) {
                throw new Error('ไม่พบ Link ID สำหรับปิดงาน');
            }

            const [error] = await closeTaskActivityV2(
                selectedTaskDetail.key,
                selectedTask.empcode
            );
            if (error) {
                throw new Error(error.message);
            }

            handleCloseTicketDetailModal();
            await refreshTaskInbox();
            Swal.fire('Success!', 'ปิดงานสำเร็จ', 'success');
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error!', error.message, 'error');
            }
        } finally {
            loader.hide();
        }
    }, [handleCloseTicketDetailModal, selectedTask, selectedTaskDetail]);

    const handleClickCancelTask = useCallback(async () => {
        try {
            loader.show();

            if (!selectedTaskDetail) {
                return;
            }
            if (!selectedTask?.empcode) {
                throw new Error('ไม่พบ Link ID สำหรับยกเลิกงาน');
            }

            const [error] = await cancelTaskActivityV2(
                selectedTaskDetail.key,
                selectedTask.empcode
            );
            if (error) {
                throw new Error(error.message);
            }

            handleCloseTicketDetailModal();
            await refreshTaskInbox();
            Swal.fire('Success!', 'ยกเลิกงานสำเร็จ', 'success');
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error!', error.message, 'error');
            }
        } finally {
            loader.hide();
        }
    }, [handleCloseTicketDetailModal, selectedTask, selectedTaskDetail]);

    const handleUpdateTaskProgress = useCallback(
        async (code: string) => {
            if (!selectedTaskDetail?.key || !selectedTask?.empcode) {
                Swal.fire('Error!', 'ข้อมูลสำหรับอัปเดตความคืบหน้าไม่ครบถ้วน', 'error');
                return false;
            }

            try {
                loader.show();
                const objectLink =
                    selectedTaskDetail.ticket?.object_link || selectedTaskDetail.key;
                const [error] = await updateTaskProgressV2(
                    objectLink,
                    selectedTask.empcode,
                    code
                );
                if (error) throw new Error(error.message);

                setSelectedTaskDetail(currentDetail =>
                    currentDetail ? { ...currentDetail, status: code } : currentDetail
                );

                const [detailError, updatedDetail] = await getTaskActivityV2({
                    key: selectedTask.aobjectlink,
                    link_id: selectedTask.empcode
                });
                if (detailError) throw new Error(detailError.message);
                if (!updatedDetail) throw new Error('ไม่พบข้อมูล Ticket หลังอัปเดตความคืบหน้า');

                setSelectedTaskDetail({
                    ...updatedDetail,
                    status: code,
                    task_status_list:
                        updatedDetail.task_status_list?.length
                            ? updatedDetail.task_status_list
                            : selectedTaskDetail.task_status_list
                });

                void refreshTaskInbox({ silent: true });
                Swal.fire('Success!', 'อัปเดตความคืบหน้าสำเร็จ', 'success');
                return true;
            } catch (error) {
                Swal.fire(
                    'Error!',
                    error instanceof Error ? error.message : 'ไม่สามารถอัปเดตความคืบหน้าได้',
                    'error'
                );
                return false;
            } finally {
                loader.hide();
            }
        },
        [selectedTask, selectedTaskDetail, refreshTaskInbox]
    );

    const handleSubmitComment = useCallback(
        async (input: CommentReplyInput) => {
            const currentLinkId = linkId || selectedTask?.empcode;
            const detailKey = selectedTaskDetail?.key || selectedTask?.aobjectlink;
            const objectLink =
                selectedTaskDetail?.ticket?.object_link || detailKey || input.objectLink;

            if (!currentLinkId || !detailKey || !objectLink) {
                Swal.fire('Error!', 'ข้อมูลสำหรับส่งข้อความไม่ครบถ้วน', 'error');
                return false;
            }

            try {
                const [error] = await createTaskRemarkV2(objectLink, input.message);
                if (error) throw new Error(error.message);

                const [detailError, updatedDetail] = await getTaskActivityV2({
                    key: detailKey,
                    link_id: currentLinkId
                });

                if (!detailError && updatedDetail) {
                    setSelectedTaskDetail(updatedDetail);
                } else {
                    Swal.fire(
                        'ส่งข้อความสำเร็จ',
                        'ส่งข้อความแล้ว แต่ยังโหลดข้อความล่าสุดไม่ได้',
                        'warning'
                    );
                }

                return true;
            } catch (error) {
                Swal.fire(
                    'Error!',
                    error instanceof Error ? error.message : 'ไม่สามารถส่งข้อความได้',
                    'error'
                );
                return false;
            }
        },
        [linkId, selectedTask, selectedTaskDetail]
    );

    return {
        email,
        linkId,
        filter,
        displayStyle,
        tasks,
        isLoadingMore,
        isRefreshingTaskInbox,
        filteredTasks,
        dateRange,
        search,
        showSupportModal,
        showCreateTicketModal,
        selectedTask,
        selectedTaskDetail,
        showTaskDetailModal,
        setDateRange,
        setDisplayStyle,
        setFilter,
        setSearch,
        handleClickTicketDetail,
        handleCloseTicketDetailModal,
        handleClickCloseTask,
        handleClickCancelTask,
        handleUpdateTaskProgress,
        handleSubmitComment,
        setShowSupportModal,
        setShowCreateTicketModal,
        handleOpenSupportModal,
        handleOpenCreateTicketModal,
        refreshTaskInbox,
        onReplyEmail
    };
}

export default TicketV2ViewModel;
